<template>
  <div>
    <div @click="backToCalendar">
      <span class="header-title"
        >&lt; {{ $t("student.attendance") }}: {{ formData.name }}</span
      >
    </div>
    <md-card>
      <md-card-header>
        <div class="md-layout">
          <div class="md-layout-item md-size-40 md-small-size-100">
            <p class="text-[#0d47a1] my-3 font-bold text-xl">
              <i class="far fa-calendar-alt"></i> {{ rangeDateText }},
              {{ rangeTimeText }}
            </p>
            <p class="text-[#0d47a1] my-3 font-medium text-lg">
              {{ $t("attendance_report.student.teacher_name") }}:
              {{ teacherName }}
            </p>
            <p class="text-[#0d47a1] my-3 font-medium text-lg">
              {{ $t("dashboard.synthetic.total_students") }}:
              {{ formData.students && formData.students.length }}
            </p>
          </div>
          <div
            class="md-layout-item md-size-60 md-small-size-100 md-layout mt-3"
          >
            <div class="md-layout-item md-size-100">
              <table class="statistical-table w-full text-center">
                <tr>
                  <th class="statistical-table-item">
                    {{ $t("attendance_report.student.present") }}
                  </th>
                  <th class="statistical-table-item">
                    {{ $t("attendance_report.student.excused_absence") }}
                  </th>
                  <th class="statistical-table-item">
                    {{ $t("attendance_report.student.excused_tardy") }}
                  </th>
                  <th class="statistical-table-item">
                    {{ $t("attendance_report.student.unexcused_absence") }}
                  </th>
                  <th class="statistical-table-item">
                    {{ $t("attendance_report.student.unexcused_tardy") }}
                  </th>
                </tr>
                <tr>
                  <td class="statistical-table-item">
                    {{ formData.cout_present }}
                  </td>
                  <td class="statistical-table-item">
                    {{ formData.cout_ex_absence }}
                  </td>
                  <td class="statistical-table-item">
                    {{ formData.cout_ex_tardy }}
                  </td>
                  <td class="statistical-table-item">
                    {{ formData.cout_unex_absence }}
                  </td>
                  <td class="statistical-table-item">
                    {{ formData.cout_unex_tardy }}
                  </td>
                </tr>
              </table>
            </div>

            <div class="md-layout-item md-size-100 md-layout mt-3">
              <div class="md-layout-item md-size-33">
                {{ $t("attendance_report.student.excused_absence") }} - EA
              </div>
              <div class="md-layout-item md-size-33">
                {{ $t("attendance_report.student.unexcused_absence") }} - UA
              </div>
              <div class="md-layout-item md-size-33">
                {{ $t("attendance_report.student.present") }} - P
              </div>
              <div class="md-layout-item md-size-33">
                {{ $t("attendance_report.student.excused_tardy") }} - ET
              </div>
              <div class="md-layout-item md-size-33">
                {{ $t("attendance_report.student.unexcused_tardy") }} - UT
              </div>
            </div>
          </div>
        </div>
      </md-card-header>

      <dropdown-button
        :title="$t('common.bulk_actions')"
        class="bulk-selection ml-9"
        :actions="bulkSelection"
        :btn-classes="'md-success'"
      ></dropdown-button>

      <md-card-content v-if="$can(Attendance.actions.list, Attendance.subject)">
        <validation-form @on-submit="attendanceSave">
          <md-table :key="tableIndex" class="flex-nowrap">
            <md-table-row class="attendance-table w-full">
              <md-table-head>{{ $t("common.student_name") }}</md-table-head>
              <md-table-head>{{ $t("common.student_code") }}</md-table-head>
              <md-table-head class="w-[10%] pr-4">{{
                $t("common.status")
              }}</md-table-head>
              <md-table-head class="w-[40%]">{{
                $t("smi_sri.comment")
              }}</md-table-head>
            </md-table-row>
            <md-table-row
              v-for="(student, studentIndex) in formData.students"
              :key="studentIndex"
            >
              <md-table-cell class="w-[25%] align-bottom">
                <div class="my-3 leading-9">
                  <md-avatar class="mr-2">
                    <img :src="avatarUrl(student)" alt="Avatar" />
                  </md-avatar>
                  {{ student.users && student.users.user_no_sql.full_name }}
                </div>
              </md-table-cell>
              <md-table-cell class="align-bottom">
                <div class="my-3 leading-9">
                  {{ student.users && student.users.user_no_sql.student_code }}
                </div>
              </md-table-cell>
              <md-table-cell class="w-[10%] pr-4 align-bottom">
                <validation-field
                  :validations="statusValidations"
                  :name="`status ${studentIndex + 1}`"
                >
                  <md-select v-model="student.status" name="status" id="status">
                    <md-option
                      v-for="(stt, idx) in statusOptions"
                      :value="stt.value"
                      :key="'stt' + idx"
                      :hidden="stt.value === ' '"
                      required
                      >{{ stt.label }}</md-option
                    >
                  </md-select>
                </validation-field>
              </md-table-cell>
              <md-table-cell class="align-bottom">
                <md-field>
                  <md-textarea
                    class="max-h-[100px] min-h-[36px]"
                    md-autogrow
                    :placeholder="$t('smi_sri.comment')"
                    v-model="student.users.attendance_description"
                  ></md-textarea>
                </md-field>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <div class="md-layout-item md-size-100 md-layout p-0">
            <label class="text-[17px] text-[#333] font-bold py-2">{{
              $t("schedule.class_comment")
            }}</label>
            <div class="md-layout-item md-size-100 p-0 border border-gray-300">
              <md-field>
                <md-textarea
                  :placeholder="$t('smi_sri.comment')"
                  v-model="formData.comment"
                ></md-textarea>
              </md-field>
            </div>
          </div>

          <!-- form submit -->
          <div class="from-submit mt-5 text-center">
            <md-button class="md-raised mr-5" @click="backToCalendar">{{
              $t("common.cancel")
            }}</md-button>
            <md-button
              class="md-success ml-4"
              type="submit"
              v-if="$can(Attendance.actions.edit, Attendance.subject)"
            >
              {{ $t("common.save") }}
            </md-button>
          </div>
        </validation-form>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import { hasHistory, genErrMsgOj, Message, getId } from "@/helpers/utils";
import PageLayout from "@/pages/PageLayout.vue";
import DropdownButton from "@/components/Base/DropdownButton";
import { service_names } from "@/constants/Services";
import cloneDeep from "lodash/cloneDeep";
import { Attendance, All } from "@/constants/Permissions";
import { TEACHER_ASSIGNMENTS } from "@/constants";
import {
  subtractDayOfDate,
  formatDate,
  isSameDate
} from "@/helpers/datetime-helper";
import moment from "moment";

export default {
  components: { DropdownButton },
  data() {
    const self = this;
    return {
      hasFooterActions: true,
      tableIndex: 0,
      teacherName: "",
      rangeTimeText: "",
      Attendance,
      All,
      rangeDateText: "",
      statusOptions: [
        {
          value: " ",
          label: "--"
        },
        {
          value: "Present",
          label: "P"
        },
        {
          value: "Ex.absence",
          label: "EA"
        },
        {
          value: "Ex.tardy",
          label: "ET"
        },
        {
          value: "Unex.absence",
          label: "UA"
        },
        {
          value: "Unex.tardy",
          label: "UT"
        }
      ],
      bulkSelection: [
        {
          content: "Present all",
          action: () => {
            self.selectionForAll("Present");
          }
        },
        {
          content: "Ex.absence all",
          action: () => {
            self.selectionForAll("Ex.absence");
          }
        },
        {
          content: "Ex.tardy all",
          action: () => {
            self.selectionForAll("Ex.tardy");
          }
        },
        {
          content: "Unex.absence all",
          action: () => {
            self.selectionForAll("Unex.absence");
          }
        },
        {
          content: "Unex.tardy all",
          action: () => {
            self.selectionForAll("Unex.tardy");
          }
        }
      ],
      formData: {}
    };
  },
  computed: {
    statusValidations() {
      return {
        format: {
          valid: value => /^\S+$/.test(value),
          message: this.$t("validation.required_field")
        }
      };
    }
  },
  created() {
    this.getStaffData();
  },
  methods: {
    backToCalendar() {
      let month =
        moment(this.formData.calendar_start_date).format("YYYY-MM") || "";
      this.$router.push(`/schedule?month=${month}`);
    },
    goToPreviousPage() {
      hasHistory() ? this.$router.go(-1) : this.$router.push("/");
    },
    selectionForAll(type) {
      this.formData.students.forEach(item => {
        item.status = type;
      });
      this.tableIndex++;
    },
    generateAttendanceForm() {
      return {
        calendar_id: this.$route.params.id,
        class_id: this.formData.id,
        comment: this.formData.comment,
        users: this.formData.students.map(student => ({
          user_id: getId(student.users.user_no_sql),
          status: student.status,
          description: student.users.attendance_description
        }))
      };
    },
    attendanceSave() {
      let month =
        moment(this.formData.calendar_start_date).format("YYYY-MM") || "";
      let formData = cloneDeep(this.generateAttendanceForm());
      this.$axios
        .post(`${service_names.school}/attendances`, formData)
        .then(() => {
          Message.success(this.$t("common.update_success"));
          this.$router.push(`/schedule?month=${month}`);
        })
        .catch(error => {
          const errorMessage = error.response?.data.messages;
          if (errorMessage) {
            this.serverValidations = genErrMsgOj(errorMessage);
          }
        });
    },
    avatarUrl(student) {
      const avatar_url = student.users && student.users.user_no_sql.avatar_url;
      if (!avatar_url) {
        return "/img/avt_placeholder.jpg";
      }
      return avatar_url;
    },
    getStaffData() {
      const calendarID = this.$route.params.id;
      this.$axios
        .get(`${service_names.school}/attendances/calendars/${calendarID}`)
        .then(response => {
          response.data.students.map(student => {
            student.status = student.users?.attendance_status ?? " ";
          });
          this.formData = response.data;
          this.formData.comment = response.data.comment?.comment || "";
          this.convertTimeZone(this.formData);
          this.setTeacherName(this.formData);
        });
    },
    setTeacherName(data) {
      data.teachers.forEach(teacher => {
        if (teacher.assignment === TEACHER_ASSIGNMENTS.PRIMARY)
          this.teacherName = teacher.users.username;
      });
    },
    convertTimeZone(data) {
      let rangeTime = this.generateRangeTime(data);
      this.rangeTimeText = rangeTime.timeText;
      this.rangeDateText = rangeTime.dateText;
    },
    generateRangeTime(event) {
      const startDate = event.calendar_start_date;
      const endDate = event.calendar_end_date;

      // refers: https://date-fns.org/v2.22.1/docs/format
      const startDateStr = formatDate(startDate, "EEEE, MMMM dd");
      const endDateStr = formatDate(endDate, "EEEE, MMMM dd");

      // FIX-ME: 00h is displayed 12h.
      const startTimeStr = formatDate(startDate, "hh:mm aaa");
      const endTimeStr = formatDate(endDate, "hh:mm aaa");

      const isAllDayEvent = event.allDay;
      const isInDayEvent =
        (isAllDayEvent && !event.end) || isSameDate(startDate, endDate);

      if (isInDayEvent) {
        if (isAllDayEvent) {
          return {
            dateText: `${startDateStr} (${this.$t("schedule.all_day")})`,
            timeText: ""
          };
        }

        return {
          dateText: startDateStr,
          timeText: `${startTimeStr} - ${endTimeStr}`
        };
      }

      if (isAllDayEvent) {
        return {
          dateText: `${startDateStr} - ${subtractDayOfDate(
            endDate,
            1,
            "dddd, MMMM DD"
          )} (${this.$t("schedule.all_day")})`,
          timeText: ""
        };
      }

      return {
        dateText: `${startTimeStr} ${startDateStr} - ${endTimeStr} ${endDateStr}`,
        timeText: ""
      };
    }
  }
};
</script>

<style lang="scss">
.header-title {
  font-weight: 500;
  font-size: 26px;
  line-height: 36px;
  letter-spacing: 0.18px;
  color: #1b1f22;
  cursor: pointer;
}
.statistical-table {
  td {
    font-size: 20px;
  }
  &-item {
    border: 1px solid;
  }
}
.attendance-table {
  .md-table-head-label {
    font-weight: 600 !important;
  }
}
</style>
